// Posts library — every logged post, filterable by platform + sortable.
// Click any card to open the PostDetailModal (view/edit/delete).
import { useEffect, useMemo, useState } from "react";
import { Search, Filter, TrendingUp, Youtube, Instagram, Film, Twitter, FileText, ArrowUpDown } from "lucide-react";
import { apiGet, PLATFORMS } from "@/lib/api";
import EmptyState from "@/components/EmptyState";
import PostDetailModal from "@/components/PostDetailModal";

const INK_55 = "var(--ink-55)";
const INK_72 = "var(--ink-72)";
const SEAL = "var(--seal)";

const ICONS = {
  youtube_long: Youtube, shorts: Film, reels: Instagram, tweet: Twitter, blog: FileText,
};

function fmt(n) {
  if (!n) return "0";
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString();
}
function engRate(m) {
  const e = (m.likes || 0) + (m.comments || 0) + (m.shares || 0) + (m.saves || 0);
  return m.views ? ((e / m.views) * 100) : 0;
}

const SORTS = [
  { id: "recent", label: "Most recent" },
  { id: "views", label: "Most views" },
  { id: "engagement", label: "Best engagement" },
  { id: "revenue", label: "Highest revenue" },
];

export default function Posts() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [platform, setPlatform] = useState("all");
  const [sort, setSort] = useState("recent");
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const data = await apiGet("/metrics", { days: 3650 });
      setRows(data || []);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    let out = [...rows];
    if (platform !== "all") out = out.filter((r) => r.platform === platform);
    if (q.trim()) {
      const s = q.toLowerCase();
      out = out.filter((r) => (r.title || "").toLowerCase().includes(s));
    }
    if (sort === "recent") out.sort((a, b) => (b.recorded_for || "").localeCompare(a.recorded_for || ""));
    if (sort === "views") out.sort((a, b) => (b.views || 0) - (a.views || 0));
    if (sort === "engagement") out.sort((a, b) => engRate(b) - engRate(a));
    if (sort === "revenue") out.sort((a, b) => (b.revenue || 0) - (a.revenue || 0));
    return out;
  }, [rows, q, platform, sort]);

  const totals = useMemo(() => {
    return filtered.reduce((acc, r) => ({
      views: acc.views + (r.views || 0),
      likes: acc.likes + (r.likes || 0),
      comments: acc.comments + (r.comments || 0),
      revenue: acc.revenue + (r.revenue || 0),
      posts: acc.posts + 1,
    }), { views: 0, likes: 0, comments: 0, revenue: 0, posts: 0 });
  }, [filtered]);

  return (
    <div className="max-w-6xl mx-auto" data-testid="posts-page">
      <div className="mb-6">
        <div className="eyebrow">i. Library</div>
        <h1 className="font-heading text-3xl md:text-4xl mt-2" style={{ fontVariationSettings: "'opsz' 96, 'wght' 500" }}>
          All <span className="font-display-italic">posts</span>
        </h1>
        <p className="text-sm mt-2 max-w-xl" style={{ color: INK_72 }}>
          Every performance snapshot you've logged. Tap any card to see the full breakdown, edit, or attach to an idea.
        </p>
      </div>

      {/* Aggregate summary */}
      {filtered.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
          {[
            { l: "Posts", v: totals.posts },
            { l: "Views", v: fmt(totals.views) },
            { l: "Likes", v: fmt(totals.likes) },
            { l: "Comments", v: fmt(totals.comments) },
            { l: "Revenue", v: `₹${totals.revenue.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`, accent: SEAL },
          ].map((s) => (
            <div key={s.l} className="rounded-lg p-3" style={{ background: "var(--surface)", border: "1px solid var(--ink-22)" }}>
              <div className="eyebrow mb-1">{s.l}</div>
              <div className="font-heading text-xl" style={{ fontVariationSettings: "'opsz' 32, 'wght' 500", color: s.accent || "var(--ink)" }}>{s.v}</div>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-2 mb-4">
        <div className="input flex items-center gap-2 flex-1">
          <Search size={13} style={{ color: INK_55 }} />
          <input
            data-testid="posts-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search titles"
            className="bg-transparent outline-none flex-1 text-sm"
          />
        </div>
        <select
          value={platform}
          onChange={(e) => setPlatform(e.target.value)}
          className="input md:max-w-[180px]"
          data-testid="posts-platform-filter"
        >
          <option value="all">All platforms</option>
          {PLATFORMS.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
        </select>
        <select
          value={sort}
          onChange={(e) => setSort(e.target.value)}
          className="input md:max-w-[180px]"
          data-testid="posts-sort"
        >
          {SORTS.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="text-sm py-10 text-center" style={{ color: INK_55 }}>Loading…</div>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={TrendingUp}
          title={rows.length === 0 ? "No posts yet" : "No matches"}
          body={rows.length === 0
            ? "Log your first published post from Analytics — views, likes, revenue. Watch trends emerge over time."
            : "Try a different platform or search term."}
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="posts-grid">
          {filtered.map((r) => {
            const Icon = ICONS[r.platform];
            const platform = PLATFORMS.find((p) => p.id === r.platform);
            const rate = engRate(r);
            return (
              <button
                key={r.id}
                onClick={() => setSelected(r.id)}
                className="card text-left transition hover:border-[var(--seal)]"
                data-testid={`post-card-${r.id}`}
                style={{ touchAction: "manipulation" }}
              >
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    {Icon && <Icon size={14} style={{ color: SEAL, flexShrink: 0 }} />}
                    <div className="min-w-0">
                      <div className="eyebrow truncate">{platform?.short || r.platform} · {r.recorded_for}</div>
                      <div className="font-heading text-base leading-tight line-clamp-2" style={{ fontVariationSettings: "'opsz' 32, 'wght' 500" }}>
                        {r.title || "Untitled"}
                      </div>
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="text-[10px] font-mono" style={{ color: rate >= 5 ? "#89c67e" : rate >= 2 ? SEAL : INK_55 }}>
                      {rate.toFixed(1)}%
                    </div>
                    <div className="text-[9px] uppercase tracking-[0.16em]" style={{ color: INK_55 }}>engaged</div>
                  </div>
                </div>
                <div className="grid grid-cols-4 gap-2">
                  {[
                    { l: "Views", v: fmt(r.views) },
                    { l: "Likes", v: fmt(r.likes) },
                    { l: "Comments", v: fmt(r.comments) },
                    { l: "Revenue", v: `₹${(r.revenue || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}` },
                  ].map((s) => (
                    <div key={s.l}>
                      <div className="text-[9px] uppercase tracking-[0.16em]" style={{ color: INK_55 }}>{s.l}</div>
                      <div className="text-sm mt-0.5" style={{ color: INK_72, fontFamily: "'Fraunces', serif", fontVariationSettings: "'opsz' 24, 'wght' 500" }}>{s.v}</div>
                    </div>
                  ))}
                </div>
              </button>
            );
          })}
        </div>
      )}

      {selected && (
        <PostDetailModal
          postId={selected}
          onClose={() => setSelected(null)}
          onChanged={() => load()}
        />
      )}
    </div>
  );
}
